import { useState } from "react";
import { cn } from "@/lib/utils";
import { formatCurrency, formatDateShort, formatTimeShort } from "@/lib/format";
import { Button } from "@/components/ui/button";
import {
  CheckCircle,
  Clock,
  CalendarDots,
  VideoCamera,
  Coins,
  CaretDown,
  CaretUp,
  Prohibit,
  XCircle,
  WarningCircle,
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { TherapyTestsPanel } from "@/components/therapist/tests/TherapyTestsPanel";
import { getSessionDisplayStatus } from "@/lib/session-display-status";

import type { TherapistTherapySession } from "@/types";

type TherapyStatus = TherapistTherapySession["status"] | "no_show" | "pending_outcome";

const STATUS_STYLES: Record<
  TherapyStatus,
  { bar: string; dot: string; bg: string; text: string; border: string; iconBg: string; label: string }
> = {
  scheduled: {
    bar: "bg-primary", dot: "bg-primary", bg: "bg-primary/10", text: "text-primary",
    border: "border-primary/20", iconBg: "bg-primary/10", label: "Zakazana",
  },
  pending_outcome: {
    bar: "bg-amber-500", dot: "bg-amber-500", bg: "bg-amber-500/10", text: "text-amber-700",
    border: "border-amber-500/25", iconBg: "bg-amber-500/10", label: "Čeka evidenciju",
  },
  completed: {
    bar: "bg-emerald-600", dot: "bg-emerald-600", bg: "bg-emerald-500/10", text: "text-emerald-700",
    border: "border-emerald-500/20", iconBg: "bg-emerald-500/10", label: "Završena",
  },
  cancelled: {
    bar: "bg-destructive", dot: "bg-destructive", bg: "bg-destructive/10", text: "text-destructive",
    border: "border-destructive/20", iconBg: "bg-destructive/10", label: "Otkazana",
  },
  no_show: {
    bar: "bg-orange-500", dot: "bg-orange-500", bg: "bg-orange-500/10", text: "text-orange-700",
    border: "border-orange-500/25", iconBg: "bg-orange-500/10", label: "Nedolazak",
  },
};

function endTime(scheduledAt: string, durationMinutes: number): string {
  const end = new Date(new Date(scheduledAt).getTime() + durationMinutes * 60000);
  return formatTimeShort(end.toISOString());
}

interface SessionCardProps {
  session: TherapistTherapySession;
  onReschedule: (session: TherapistTherapySession) => void;
  onComplete?: (session: TherapistTherapySession) => void;
  onCancel?: (session: TherapistTherapySession) => void;
  onNoShow?: (session: TherapistTherapySession) => void;
  onWithdrawCancelRequest?: (session: TherapistTherapySession) => void;
  completePending?: boolean;
  noShowPending?: boolean;
}

export function SessionCard({
  session,
  onReschedule,
  onComplete,
  onCancel,
  onNoShow,
  onWithdrawCancelRequest,
  completePending,
  noShowPending,
}: SessionCardProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const displayStatus = getSessionDisplayStatus(session) as TherapyStatus;
  const isPendingOutcome = displayStatus === "pending_outcome";
  const status = session.status as TherapyStatus;
  const s = STATUS_STYLES[displayStatus] ?? STATUS_STYLES.scheduled;
  const paidOut = !!session.payoutId;
  const earns = status === "completed" || status === "no_show";
  const hasUpcomingActions = session.status === "scheduled" && !isPendingOutcome;
  const hasPendingOutcomeActions = isPendingOutcome && (onComplete || onNoShow);
  const showActionFooter = hasUpcomingActions || hasPendingOutcomeActions || earns;
  const StatusIcon =
    displayStatus === "cancelled"
      ? XCircle
      : displayStatus === "no_show"
        ? Prohibit
        : displayStatus === "pending_outcome"
          ? WarningCircle
          : Clock;

  return (
    <article className="relative overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm transition-colors hover:border-primary/30">
      <span aria-hidden className={cn("absolute inset-y-0 left-0 w-1", s.bar)} />

      <div className="px-4 py-4 pl-5 sm:px-5 sm:pl-6">
        <div className="flex gap-3">
          <div className={cn("flex size-11 shrink-0 items-center justify-center rounded-xl", s.iconBg)}>
            <StatusIcon className={cn("size-5", s.text)} weight="duotone" />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span
                    className="text-[15px] font-semibold tabular-nums text-foreground"
                    style={{ fontFamily: "var(--font-heading)" }}
                  >
                    {formatTimeShort(session.scheduledAt)}
                    <span className="font-normal text-muted-foreground">
                      {" – "}
                      {endTime(session.scheduledAt, session.durationMinutes)}
                    </span>
                  </span>
                  <span
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em]",
                      s.bg, s.border, s.text,
                    )}
                    style={{ fontFamily: "var(--font-heading)" }}
                  >
                    <span className={cn("size-1.5 rounded-full", s.dot)} />
                    {s.label}
                  </span>
                </div>
                <p className="mt-0.5 text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground tabular-nums">
                  {formatDateShort(session.scheduledAt)}
                </p>

                <h3
                  className="mt-2.5 truncate text-[16px] font-semibold leading-tight text-foreground"
                  style={{ fontFamily: "var(--font-heading)" }}
                >
                  {session.clientName}
                </h3>
                <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[12px] text-muted-foreground">
                  <span className="truncate">{session.serviceName}</span>
                  <span className="text-muted-foreground/30">·</span>
                  <span className="tabular-nums">{session.durationMinutes} min</span>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                    {session.modality === "in_person" ? t("booking.modalityInPerson") : t("booking.modalityOnline")}
                  </span>
                </p>
              </div>

              {earns && session.therapistEarnings != null && (
                <div className="flex shrink-0 flex-col items-end">
                  <span
                    className="text-[18px] font-semibold leading-none text-primary tabular-nums"
                    style={{ fontFamily: "var(--font-heading)" }}
                  >
                    {formatCurrency(session.therapistEarnings)}
                  </span>
                  <span className="mt-1 text-[9px] font-medium uppercase tracking-[0.16em] text-muted-foreground/60">
                    {t("therapist.therapies.earningsLabel")}
                  </span>
                </div>
              )}
            </div>

            {status === "no_show" && (
              <div className="mt-3 flex items-center gap-2 rounded-lg border border-orange-500/20 bg-orange-500/10 px-3 py-2 text-[11px] font-medium text-orange-700">
                <WarningCircle className="size-3.5 shrink-0" weight="fill" />
                {t("therapist.therapies.noShowBilledNote")}
              </div>
            )}
          </div>
        </div>

        {/* Action footer */}
        {showActionFooter && (
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border/60 pt-3">
            {hasUpcomingActions && (
              <>
                {session.meetLink && (
                  <Button size="sm" asChild className="rounded-full">
                    <a href={session.meetLink} target="_blank" rel="noopener noreferrer">
                      <VideoCamera className="size-3.5" weight="fill" />
                      {t("therapist.therapies.join")}
                    </a>
                  </Button>
                )}
                {session.cancelRequestId ? (
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-800">
                      Otkazivanje u toku
                    </span>
                    {onWithdrawCancelRequest && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full border-border/70 bg-transparent shadow-none"
                        onClick={() => onWithdrawCancelRequest(session)}
                      >
                        Povuci zahtjev
                      </Button>
                    )}
                  </div>
                ) : (
                  <>
                    <Button size="sm" variant="outline" onClick={() => onReschedule(session)} className="rounded-full border-border/60">
                      <CalendarDots className="size-3.5" />
                      {t("therapist.therapies.reschedule")}
                    </Button>
                    {onCancel && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onCancel(session)}
                        className="rounded-full border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
                      >
                        {t("therapist.therapies.cancel")}
                      </Button>
                    )}
                  </>
                )}
              </>
            )}

            {isPendingOutcome && (
              <>
                {onComplete && (
                  <Button size="sm" onClick={() => onComplete(session)} disabled={completePending} className="rounded-full">
                    <CheckCircle className="size-3.5" weight="fill" />
                    {t("therapist.therapies.markComplete")}
                  </Button>
                )}
                {onNoShow && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onNoShow(session)}
                    disabled={noShowPending}
                    className="rounded-full border-orange-500/35 text-orange-700 hover:bg-orange-500/10 hover:text-orange-700"
                  >
                    <Prohibit className="size-3.5" />
                    {t("therapist.therapies.markNoShow")}
                  </Button>
                )}
              </>
            )}

            {earns && (
              <span
                className={cn(
                  "ml-auto inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em]",
                  paidOut
                    ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-700"
                    : "border-amber-500/20 bg-amber-500/10 text-amber-700",
                )}
                style={{ fontFamily: "var(--font-heading)" }}
              >
                {paidOut ? (
                  <>
                    <CheckCircle className="size-3" weight="fill" />
                    {t("therapist.therapies.paidOutBadge")}
                    {session.payoutPaidAt && (
                      <span className="tabular-nums"> · {formatDateShort(session.payoutPaidAt)}</span>
                    )}
                  </>
                ) : (
                  <>
                    <Clock className="size-3" weight="fill" />
                    {t("therapist.therapies.pendingPayoutBadge")}
                  </>
                )}
              </span>
            )}
          </div>
        )}

        {session.status === "cancelled" && (
          <div className="mt-3 flex items-center gap-2 border-t border-border/60 pt-3 text-[11px] text-muted-foreground/70">
            <Coins className="size-3.5 text-muted-foreground/40" />
            <span>{t("therapist.therapies.cancelledNote")}</span>
          </div>
        )}

        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="mt-2 flex items-center gap-1 text-xs text-primary"
        >
          {expanded ? <CaretUp size={12} /> : <CaretDown size={12} />}
          {expanded ? t("tests.therapist.therapyPanel.hide") : t("tests.therapist.therapyPanel.show")}
        </button>
        {expanded && <TherapyTestsPanel therapyId={session.id} />}
      </div>
    </article>
  );
}
